import { makeAutoObservable } from 'mobx';

import { formatNumberToCurrencyString } from '@utils';

import { store } from './store';
import { CloudFunctionsService, FirebaseService } from '../services';
import {
  EvaluationResult,
  EvaluationName,
  PrequalDecision,
  PrequalDecisionEnum,
  QualificationStatus,
  QualificationStatusEnum,
  EvaluationCardOption,
  DigitalEnrollmentPrograms,
  Program,
  IpBlacklist,
  PrequalResult,
  SavingsEstimateResult,
  ProgramRankingResult,
} from '../types';
import { rollbar } from '../utils/rollbar';

const IPIFY_TIMEOUT_MS = 15_000;

type PublicIpLookup = { ip: string } | { ip: null; error: string };

const FALLBACK_PROGRAM_ORDER: EvaluationName[] = [
  'Debt Management',
  'Debt Settlement',
  'Consolidation Loan',
];

const { QUALIFY } = PrequalDecisionEnum;
const { MULTIPLE_QUALIFIED, SINGLE_QUALIFIED, UNQUALIFIED } =
  QualificationStatusEnum;

export default class DecisionEngineStore {
  firebaseService = new FirebaseService(new CloudFunctionsService());
  prequalResults: PrequalResult[] = [];
  savingsEstimates: SavingsEstimateResult[] = [];
  programRanking: EvaluationName[] = [];
  ipBlacklist: IpBlacklist = { blacklist: [] };
  isBlacklisted: boolean = false;
  isUserEligibleDigitalOptIn = false;

  constructor() {
    makeAutoObservable(this);
  }

  private filterResultsByDecision = (
    decision: PrequalDecision
  ): PrequalResult[] => {
    return this.prequalResults
      .filter((result) => result.decision === decision)
      .sort(this.sortDecisionEngineResults);
  };

  private sortDecisionEngineResults = (
    a: PrequalResult,
    b: PrequalResult
  ): number => {
    const order = this.programRanking.length
      ? this.programRanking
      : FALLBACK_PROGRAM_ORDER;
    // An unranked name must not outrank a ranked one, so it sorts last.
    const rank = (name: EvaluationName) => {
      const index = order.indexOf(name);
      return index === -1 ? order.length : index;
    };
    return rank(a.name) - rank(b.name);
  };

  private setDecisionEngineResults = (results: EvaluationResult[]) => {
    this.prequalResults = [];
    this.savingsEstimates = [];
    this.programRanking = [];

    function isPrequalResult(
      result: EvaluationResult
    ): result is PrequalResult {
      return result.rule.startsWith('prequal');
    }

    function isSavingsEstimate(
      result: EvaluationResult
    ): result is SavingsEstimateResult {
      return (
        result.rule.startsWith('plan') &&
        typeof result.output?.estimatedSavings === 'number'
      );
    }

    function isProgramRanking(
      result: EvaluationResult
    ): result is ProgramRankingResult {
      const r = result as ProgramRankingResult;
      return (
        r.useForProgramRanking === true ||
        result.rule === 'program_options_ranking'
      );
    }

    results.forEach((result) => {
      if (isPrequalResult(result)) {
        this.prequalResults.push(result);
      } else if (isSavingsEstimate(result)) {
        const { estimatedSavings } = result.output as {
          estimatedSavings: number;
        };
        result.decision = estimatedSavings;
        this.savingsEstimates.push(result);
      } else if (isProgramRanking(result)) {
        this.programRanking = result.decision
          .split(',')
          .map((item) => item.trim()) as EvaluationName[];
      }
    });

    const qualified = this.prequalResults.filter(
      (result) => result.decision === QUALIFY
    ).length;
    const usesOrder =
      qualified > 1 || this.prequalResults.length - qualified > 1;

    if (!this.programRanking.length && usesOrder) {
      rollbar.warning(
        'Missing programs order from evaluation results array. Using fallback order.'
      );
    }
  };

  //Returns formatted savings estimate when we have a result from the evaluation for this program.
  getSavingsEstimate = (programName: EvaluationName) => {
    const savingsEstimate = this.savingsEstimates.find(
      (savings) => savings.name === programName
    )?.decision;

    if (typeof savingsEstimate === 'number')
      return formatNumberToCurrencyString(Math.round(savingsEstimate));
    return null;
  };

  // Returns the 'output' property object that can include properties like totalEligibleDebt,
  // programTimePeriod, minPaymentMonths, estimatedSavings, etc.
  getSavingsEstimateResult = (
    programName: EvaluationName
  ): Record<string, unknown> | undefined => {
    const savingsEstimate = this.savingsEstimates.find(
      (savings) => savings.name === programName
    );
    return savingsEstimate?.output;
  };

  get hasActiveSavingsRule(): boolean {
    const recommended = this.recommendedProgram;
    if (!recommended) return false;
    return store.partnerStore.hasSavingsRuleForProgram(recommended.name);
  }

  setIpBlacklist = (blackListItems: IpBlacklist) => {
    this.ipBlacklist = blackListItems;
  };

  setBlackListed = (blacklisted: boolean) => {
    this.isBlacklisted = blacklisted;
  };

  get qualifiedResults() {
    return this.filterResultsByDecision(QUALIFY);
  }

  get isUserUnqualified() {
    return !!this.prequalResults.length && !this.qualifiedResults.length;
  }

  get qualificationStatus(): QualificationStatus {
    if (this.qualifiedResults.length > 1) {
      return MULTIPLE_QUALIFIED;
    }

    if (this.qualifiedResults.length === 1) {
      return SINGLE_QUALIFIED;
    }

    return UNQUALIFIED;
  }

  get isQualified() {
    return (
      this.qualificationStatus === SINGLE_QUALIFIED ||
      this.qualificationStatus === MULTIPLE_QUALIFIED
    );
  }

  get isDebtSettlementQualified() {
    return this.qualifiedResults.some(
      (result) => result.name === 'Debt Settlement'
    );
  }

  get isDebtManagementQualified() {
    return this.qualifiedResults.some(
      (result) => result.name === 'Debt Management'
    );
  }

  get isConsolidationLoanQualified() {
    return this.qualifiedResults.some(
      (result) => result.name === 'Consolidation Loan'
    );
  }

  get recommendedProgram() {
    return this.qualifiedResults[0] || undefined;
  }

  filterOptionsByQualification = (
    evaluationOptions: { [key: string]: EvaluationCardOption },
    decision: PrequalDecisionEnum
  ): EvaluationCardOption[] => {
    return this.filterResultsByDecision(decision).map((result) => ({
      ...evaluationOptions[result.rule],
      decision: result.decision,
    }));
  };

  fetchBlacklist = async () => {
    try {
      const blacklist =
        await this.firebaseService.getFirebaseRemoteConfig('ip_blacklist');

      if (!blacklist) {
        rollbar.warn('Remote Config key "ip_blacklist" not found or empty.');
        this.setIpBlacklist({ blacklist: [] });
        return;
      }
      this.setIpBlacklist(blacklist);
    } catch {
      rollbar.error('Failed to fetch ip_blacklist from remote config:');
      this.setIpBlacklist({ blacklist: [] });
    }
  };

  /**
   * Resolves the visitor's public IP via ipify, or returns failure details.
   * Never throws; the caller reports only when neither lookup succeeds.
   *
   * A non-ok response must never reach .json(): proxies and web filters
   * routinely categorize IP-lookup hostnames as anonymizers and answer with
   * their own HTML block page, which causes an error when parsed as JSON.
   */
  private lookupPublicIp = async (url: string): Promise<PublicIpLookup> => {
    try {
      // ipify is a third party with no availability guarantee; bound the
      // lookup so a stalled request can't hang the blacklist check.
      const response = await fetch(url, {
        signal: AbortSignal.timeout(IPIFY_TIMEOUT_MS),
      });

      if (!response.ok) {
        return { ip: null, error: `HTTP ${response.status}` };
      }

      const { ip } = await response.json();

      return typeof ip === 'string' && ip.length > 0
        ? { ip }
        : { ip: null, error: 'Response did not contain an IP address' };
    } catch (error) {
      return {
        ip: null,
        error: error instanceof Error ? error.message : String(error),
      };
    }
  };

  checkIfIpBlacklisted = async () => {
    try {
      await this.fetchBlacklist();

      const [ipv6, ipv4] = await Promise.all([
        this.lookupPublicIp('https://api64.ipify.org?format=json'),
        this.lookupPublicIp('https://api.ipify.org?format=json'),
      ]);

      if (ipv6.ip === null && ipv4.ip === null) {
        rollbar.warning('IP blacklist check could not resolve a public IP', {
          ipv6,
          ipv4,
        });
      }

      // The check fails open: an address we couldn't resolve is never a match,
      // so a blocked or unavailable lookup leaves the form accessible.
      const blacklistedIps = this.ipBlacklist.blacklist ?? [];
      if (
        (ipv6.ip && blacklistedIps.includes(ipv6.ip)) ||
        (ipv4.ip && blacklistedIps.includes(ipv4.ip))
      ) {
        this.setBlackListed(true);
      }
    } catch (error) {
      rollbar.warning('IP blacklist check failed', { error });
    }
  };

  sendEngagementToDecisionEngine = async (): Promise<void> => {
    if (this.prequalResults.length === 0) {
      store.commonStore.setLoading(true);
      try {
        await store.partnerStore.programsReady;
        const language = store.initializeStore.options.language || 'en';
        const results = await this.firebaseService.sendEvaluation(
          store.userStore.user!,
          language
        );

        this.setDecisionEngineResults(results);
        await this.determineOptInEligibleStatus();
      } catch (error) {
        rollbar.error(
          'Decision engine store: Error getting the evaluation results',
          error as Error
        );
      } finally {
        store.commonStore.setLoading(false);
      }
    }
  };

  private determineTestPrograms =
    async (): Promise<DigitalEnrollmentPrograms> => {
      const token = store.initializeStore.token;
      const testPrograms: DigitalEnrollmentPrograms = {
        'Debt Settlement': false,
        'Debt Management': false,
        'Consolidation Loan': false,
      };
      if (token) {
        const result = await this.firebaseService.getRequiredPrograms({
          token,
        });
        const programs: Program[] = result.data;
        const programStatus = Object.fromEntries(
          programs.map((program) => [program.name, program.active])
        );
        testPrograms['Debt Settlement'] = programStatus['Debt Settlement'];
        testPrograms['Debt Management'] = programStatus['Debt Management'];
        testPrograms['Consolidation Loan'] =
          programStatus['Consolidation Loan'];
      }
      return testPrograms;
    };

  determineOptInEligibleStatus = async (): Promise<void> => {
    if (this.isUserUnqualified) return;

    const { enableDigitalOptIn } = store.featureFlagStore.featureFlags;
    if (!enableDigitalOptIn) return;

    const programs = await this.determineTestPrograms();

    if (
      (programs['Debt Settlement'] && this.isDebtSettlementQualified) ||
      (programs['Debt Management'] && this.isDebtManagementQualified) ||
      // EC-1282: Consolidation Loan mirrors the DSP digital-enrollment handoff.
      (programs['Consolidation Loan'] && this.isConsolidationLoanQualified)
    ) {
      this.isUserEligibleDigitalOptIn = true;
    }
  };
}
